import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  compareDateOnlyStringsDesc,
  dateOnlyStringToCalendarDate,
  formatDateOnlyForDisplay,
  localCalendarDateToIsoDateString,
  normalizeCalendarDayToLocalNoon,
  normalizeToIsoDateOnly,
} from "helpers/date";
import useDocumentTitle from "hooks/useDocumentTitle";
import { BaseRateFileUpdateDialog } from "pages/PayerRules/components/BaseRateFileUpdateDialog";
import { BaseRateFileUploadDialog } from "pages/PayerRules/components/BaseRateFileUploadDialog";
import { useBaseRateFileKindConversion } from "pages/PayerRules/components/useBaseRateFileKindConversion";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Tag } from "primereact/tag";
import { useContext, useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetAllBaseRateFilesQuery } from "store/queries/baseRateFiles";
import {
  useGetContractFeeScheduleQuery,
  useGetContractFeeSchedulesQuery,
  useGetLinkedProviderConflictsMutation,
  useReplaceLinkedProviderMutation,
  useUpdateContractFeeScheduleMutation,
  useAttachCarveoutBaseRateFilesMutation,
  useDeleteCarveoutMutation,
  useUpdateCarveoutRateMutation,
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation,
  IContractTermInGroup,
  ICarveoutAttachment,
  RateCalculation,
  RateType,
  normalizeRateType,
  resolveRateCalculationBaseRateDisplayName,
} from "store/queries/contractFeeSchedules";
import { useGetAllExplodingFeeSchedulesQuery } from "store/queries/explodingFeeSchedule";
import { useGetInpatientRulesetsQuery } from "store/queries/inpatientRulesets";
import { useGetAllPackageRulesetsQuery } from "store/queries/packageRuleset";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetCaseRateRulesetsQuery } from "store/queries/pricing";
import { useGetAllDiscountRulesetsQuery } from "store/queries/procedureDiscountRuleset";
import { useGetAllProvidersQuery } from "store/queries/providers";

import RateCell from "./components/RateCell";
import UpdateTermFields from "./components/UpdateTermFields";
import { ContractUpdateTerm, normalizeUpdateTerms } from "./helpers";
import {
  linkedPayerCopy,
  linkedPayerTypeForContractPayer,
} from "./linkedPayers";
import {
  IOwnContractConflict,
  isOwnContractConflictMessage,
  LINKED_PROVIDERS_COPY,
  disconnectConfirmCopy,
  linkedProviderOptions,
  providersAdded,
  providersRemoved,
  REPLACE_NEEDS_ACTIVE_CONTRACT,
  replaceConfirmCopy,
  withoutContractProvider,
} from "./linkedProviders";

const UPLOAD_NEW_SENTINEL = "__upload_new__";

interface IFormData {
  providerId: string;
  payerId: number | null;
  effectiveDate: Date | null;
  nextUpdateDate: Date | null;
  /** CLE-2781 — only meaningful with a nextUpdateDate; normalizeUpdateTerms keeps the pair legal. */
  updateTerm: ContractUpdateTerm | null;
  updatePercent: number | null;
  rateCalculations: RateCalculation[];
  attachmentFiles: File[];
  medicareAdvantagePayerIds: number[];
  linkedProviderIds: string[];
  notes?: string;
  implantTerms?: string;
}

export function EditContractFeeSchedulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, contractId } = useParams<{ id: string; contractId?: string }>();

  // In hospital context, use contractId; in admin context, use id
  const actualContractId = contractId || id;
  const hospitalId = contractId ? id : null; // If contractId exists, then id is the hospital ID
  useDocumentTitle("Edit Contract Fee Schedule");
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);

  // Form state
  const [formData, setFormData] = useState<IFormData>({
    providerId: "",
    payerId: null,
    effectiveDate: null,
    nextUpdateDate: null,
    updateTerm: null,
    updatePercent: null,
    rateCalculations: [],
    attachmentFiles: [],
    medicareAdvantagePayerIds: [],
    linkedProviderIds: [],
  });

  // New rate calculation state
  const [newRateCalc, setNewRateCalc] = useState<{
    baseRateFileId: string;
    ratePercent: number;
    rateType: RateType;
  }>({
    baseRateFileId: "",
    ratePercent: 100,
    rateType: "percent",
  });

  /** Library carveout BaseRateFile rows (with %) to link on save (POST carveout links). */
  const [pendingCarveouts, setPendingCarveouts] = useState<
    ICarveoutAttachment[]
  >([]);

  /** CLE-2573 — key of the rate row whose rate is currently being edited in place. */
  const [editingRateKey, setEditingRateKey] = useState<string | null>(null);

  /**
   * CLE-2573 — corrected rates for already attached carve-outs, keyed by carveout
   * id and applied on save. Deliberately queued rather than written immediately:
   * a mid-edit write invalidates the contract query, and the effect that re-seeds
   * `formData` from it would discard every other unsaved change on the page.
   */
  const [carveoutRateEdits, setCarveoutRateEdits] = useState<
    Record<string, { ratePercent: number; rateType: RateType }>
  >({});

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  /** Base rate / carveout file id being edited from a rate row (opens the update modal). */
  const [updateDialogFileId, setUpdateDialogFileId] = useState<string | null>(
    null
  );

  // Data fetching
  const { data: contractData, isLoading: isLoadingContract } =
    useGetContractFeeScheduleQuery(actualContractId ?? "", {
      skip: !actualContractId,
    });
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();
  const { data: baseRateFiles } = useGetAllBaseRateFilesQuery({
    accountId: hospitalId ?? undefined,
  });
  const { data: mpdrRulesets } = useGetAllDiscountRulesetsQuery();
  const { data: caseRateRulesets } = useGetCaseRateRulesetsQuery();
  const { data: packageRulesetsAll } = useGetAllPackageRulesetsQuery();
  const { data: inpatientRulesetsAll } = useGetInpatientRulesetsQuery();
  const { data: explodingFeeSchedulesAll } =
    useGetAllExplodingFeeSchedulesQuery();
  const [updateContract] = useUpdateContractFeeScheduleMutation();
  const [getLinkedProviderConflicts] = useGetLinkedProviderConflictsMutation();
  const [replaceLinkedProvider] = useReplaceLinkedProviderMutation();
  const [attachCarveoutBaseRateFiles] =
    useAttachCarveoutBaseRateFilesMutation();
  const [deleteCarveout] = useDeleteCarveoutMutation();
  const [updateCarveoutRate] = useUpdateCarveoutRateMutation();
  const [uploadAttachment] = useUploadAttachmentMutation();
  const [deleteAttachment] = useDeleteAttachmentMutation();

  const loadedContract = contractData?.data;
  const passedTerms = (
    location.state as { termsInGroup?: IContractTermInGroup[] } | null
  )?.termsInGroup;

  const { data: contractList = [] } = useGetContractFeeSchedulesQuery(
    {
      providerId: loadedContract?.providerId,
      payerId: loadedContract?.payerId,
    },
    {
      skip: !loadedContract?.providerId || loadedContract?.payerId == null,
    }
  );

  const termsFromList = useMemo(() => {
    if (!contractList.length || !actualContractId) return undefined;
    const row = contractList.find((r) => {
      if (r.id === actualContractId) return true;
      // eslint-disable-next-line no-underscore-dangle
      return !!r._termsInGroup?.some((t) => t.id === actualContractId);
    });
    // eslint-disable-next-line no-underscore-dangle
    return row?._termsInGroup;
  }, [contractList, actualContractId]);

  /** Prefer API list so new terms appear after create; use navigation state only until list loads. */
  const termsInGroup = useMemo(() => {
    if (termsFromList && termsFromList.length > 0) return termsFromList;
    if (passedTerms && passedTerms.length > 0) return passedTerms;
    return termsFromList;
  }, [termsFromList, passedTerms]);

  /**
   * Whether the term being edited is the latest (newest effective date) in its
   * group. Only the latest term may create a new base rate term / roll forward;
   * previous terms can version-update their existing file only. A lone/ungrouped
   * term is treated as latest.
   */
  const isLatestTerm = useMemo(() => {
    if (!termsInGroup || termsInGroup.length <= 1) return true;
    if (!actualContractId) return true;
    const latest = [...termsInGroup].sort((a, b) =>
      compareDateOnlyStringsDesc(a.effectiveDate, b.effectiveDate)
    )[0];
    return latest?.id === actualContractId;
  }, [termsInGroup, actualContractId]);

  const termSwitcherOptions = useMemo(() => {
    if (!termsInGroup?.length) return [];
    return [...termsInGroup]
      .sort((a, b) =>
        compareDateOnlyStringsDesc(a.effectiveDate, b.effectiveDate)
      )
      .map((t) => ({
        label: `${formatDateOnlyForDisplay(t.effectiveDate)}${
          t.endDate ? ` – ${formatDateOnlyForDisplay(t.endDate)}` : ""
        } · ${t.status}`,
        value: t.id,
      }));
  }, [termsInGroup]);

  const effectiveDateDuplicatesOtherTerm = useMemo(() => {
    if (!formData.effectiveDate || !actualContractId) return false;
    const effectiveIso = localCalendarDateToIsoDateString(
      formData.effectiveDate
    );
    if (!effectiveIso) return false;
    return (
      termsInGroup?.some((t) => {
        if (t.id === actualContractId) return false;
        const other = normalizeToIsoDateOnly(t.effectiveDate);
        return other !== undefined && other === effectiveIso;
      }) ?? false
    );
  }, [formData.effectiveDate, termsInGroup, actualContractId]);

  // Track selected provider (for backwards compat when rateType is missing)
  const selectedProvider = providers?.data?.find(
    (p: any) => p.id === formData.providerId
  );

  // Loading state for submit button
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track existing rulesets for selected provider/payer
  const [existingMpdrRulesets, setExistingMpdrRulesets] = useState<any[]>([]);
  const [existingCaseRateRulesets, setExistingCaseRateRulesets] = useState<
    any[]
  >([]);
  const [existingPackageRulesets, setExistingPackageRulesets] = useState<any[]>(
    []
  );
  const [existingInpatientRulesets, setExistingInpatientRulesets] = useState<
    any[]
  >([]);
  const [existingExplodingRulesets, setExistingExplodingRulesets] = useState<
    any[]
  >([]);

  // Initialize form data from fetched contract
  useEffect(() => {
    if (contractData?.data) {
      const contract = contractData.data;
      setFormData({
        providerId: contract.providerId,
        payerId: contract.payerId,
        effectiveDate: dateOnlyStringToCalendarDate(contract.effectiveDate),
        nextUpdateDate: contract.nextUpdateDate
          ? dateOnlyStringToCalendarDate(contract.nextUpdateDate)
          : null,
        // CLE-2781 — updatePercent is a Prisma Decimal, so it arrives as a string like "3.00".
        updateTerm: contract.updateTerm ?? null,
        updatePercent:
          contract.updatePercent === null ||
          contract.updatePercent === undefined
            ? null
            : Number(contract.updatePercent),
        rateCalculations: (contract.rateCalculations || []).map((calc) => ({
          ...calc,
          ratePercent: Number(calc.ratePercent),
          rateType: normalizeRateType(calc.rateType),
        })),
        attachmentFiles: [],
        notes: contract.notes || "",
        implantTerms: contract.implantTerms || "",
        medicareAdvantagePayerIds:
          contract.medicareAdvantagePlans?.map(
            (link: any) => link.medicareAdvantagePlanId
          ) || [],
        // CLE-2786 — the API returns the resolved providers; the form only needs their ids.
        linkedProviderIds:
          (contract as any).linkedProviders?.map((p: any) => p.id) || [],
      });
    }
  }, [contractData]);

  useEffect(() => {
    setPendingCarveouts([]);
    setCarveoutRateEdits({});
  }, [actualContractId]);

  // Fetch existing rulesets when provider/payer changes
  useEffect(() => {
    if (formData.providerId && formData.payerId) {
      // Filter existing MPDR rulesets for this provider/payer combination
      const filteredMpdrRulesets =
        mpdrRulesets?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === formData.providerId &&
            ruleset.payer?.id === formData.payerId
        ) || [];
      setExistingMpdrRulesets(filteredMpdrRulesets);

      // Filter existing case rate rulesets for this provider/payer combination
      const filteredCaseRateRulesets =
        caseRateRulesets?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === formData.providerId &&
            ruleset.payer?.id === formData.payerId
        ) || [];
      setExistingCaseRateRulesets(filteredCaseRateRulesets);

      // Filter existing package rulesets for this provider/payer combination
      const filteredPackageRulesets =
        packageRulesetsAll?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === formData.providerId &&
            ruleset.payer?.id === formData.payerId
        ) || [];
      setExistingPackageRulesets(filteredPackageRulesets);

      // Filter existing inpatient rulesets for this provider/payer combination
      const filteredInpatientRulesets =
        inpatientRulesetsAll?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === formData.providerId &&
            ruleset.payer?.id === formData.payerId
        ) || [];
      setExistingInpatientRulesets(filteredInpatientRulesets);

      // Filter existing exploding fee schedule rulesets for this provider/payer combination
      const filteredExplodingRulesets =
        explodingFeeSchedulesAll?.data?.filter(
          (ruleset: any) =>
            ruleset.provider?.id === formData.providerId &&
            ruleset.payer?.id === formData.payerId
        ) || [];
      setExistingExplodingRulesets(filteredExplodingRulesets);
    } else {
      setExistingMpdrRulesets([]);
      setExistingCaseRateRulesets([]);
      setExistingPackageRulesets([]);
      setExistingInpatientRulesets([]);
      setExistingExplodingRulesets([]);
    }
  }, [
    formData.providerId,
    formData.payerId,
    mpdrRulesets?.data,
    caseRateRulesets?.data,
    packageRulesetsAll?.data,
    inpatientRulesetsAll?.data,
    explodingFeeSchedulesAll?.data,
  ]);

  // Options for dropdowns
  const providerOptions = useMemo(() => {
    return (
      providers?.data?.map((p: any) => ({ label: p.name, value: p.id })) || []
    );
  }, [providers]);

  // CLE-2786 — every provider except this contract's own, across all accounts.
  const connectedProviderOptions = useMemo(
    () =>
      linkedProviderOptions(
        providers?.data as any,
        formData.providerId,
        loadedContract?.linkedProviders
      ),
    [providers, formData.providerId, loadedContract?.linkedProviders]
  );

  const payerOptions = useMemo(() => {
    return (
      payers?.data?.map((p: any) => ({ label: p.name, value: p.id })) || []
    );
  }, [payers]);

  const attachedCarveoutBaseRateIds = useMemo(() => {
    return new Set(
      (loadedContract?.carveOutFiles ?? [])
        .map((c) => c.baseRateFileId)
        .filter((id): id is string => typeof id === "string" && id !== "")
    );
  }, [loadedContract?.carveOutFiles]);

  // Single dropdown of both base rate files and carveouts. Carveout options are
  // tagged and hidden once already attached/pending (a carveout attaches once per
  // contract); base rate files stay selectable.
  const baseRateFileOptions = useMemo(() => {
    const pendingCarveoutIds = new Set(
      pendingCarveouts.map((c) => c.baseRateFileId)
    );
    const existing = (baseRateFiles?.data ?? [])
      .filter((f) => {
        if (f.isCarveout) {
          return (
            !attachedCarveoutBaseRateIds.has(f.id) &&
            !pendingCarveoutIds.has(f.id)
          );
        }
        return true;
      })
      .map((f) => ({
        label: f.isCarveout
          ? `${f.displayName} (Carveout, v${f.version})`
          : f.displayName,
        value: f.id,
      }));
    return [
      { label: "+ Upload new file…", value: UPLOAD_NEW_SENTINEL },
      ...existing,
    ];
  }, [baseRateFiles?.data, attachedCarveoutBaseRateIds, pendingCarveouts]);

  /** Look up a base rate file by id (to decide carveout vs base and resolve display names). */
  const baseRateFileById = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        displayName?: string;
        isCarveout?: boolean;
        version?: number;
      }
    >();
    (baseRateFiles?.data ?? []).forEach((f) => map.set(f.id, f));
    return map;
  }, [baseRateFiles?.data]);

  /**
   * CLE-2567 — files referenced by this contract's rate rows, so the convert action
   * knows up front whether each one is still detached (added in this session, so
   * convertible) or already saved onto a contract (blocked, with links).
   */
  const rateRowFileIds = useMemo(() => {
    const ids = [
      ...formData.rateCalculations.map((c) => c.baseRateFileId),
      ...(loadedContract?.carveOutFiles ?? []).map((c) => c.baseRateFileId),
      ...pendingCarveouts.map((c) => c.baseRateFileId),
    ].filter((id): id is string => typeof id === "string" && id.length > 0);
    return [...new Set(ids)];
  }, [
    formData.rateCalculations,
    loadedContract?.carveOutFiles,
    pendingCarveouts,
  ]);

  const { openConvertDialog } = useBaseRateFileKindConversion({
    fileIds: rateRowFileIds,
    accountId: hospitalId ?? undefined,
    showToast: (message) => toastElement?.show(message),
  });

  const selectedPayer = payers?.data?.find(
    (p: any) => p.id === formData.payerId
  );
  // CLE-2751: MA payers on a Medicare contract, or other Medicaid payers on a Medicaid one
  const linkablePayerType = linkedPayerTypeForContractPayer(
    selectedPayer?.type
  );
  const canLinkPayers = linkablePayerType !== null;
  const linkedCopy = linkedPayerCopy(selectedPayer?.type);

  const medicareAdvantagePayerOptions = useMemo(() => {
    return (
      payers?.data
        ?.filter(
          (p: any) => p.type === linkablePayerType && p.id !== formData.payerId
        )
        ?.map((p: any) => ({ label: p.name, value: p.id })) || []
    );
  }, [payers, linkablePayerType, formData.payerId]);

  const handleInputChange = (field: keyof IFormData, value: any) => {
    setFormData((prev) => {
      let nextVal = value;
      if (
        (field === "effectiveDate" || field === "nextUpdateDate") &&
        value instanceof Date
      ) {
        nextVal = normalizeCalendarDayToLocalNoon(value);
      }
      const updates: any = { [field]: nextVal };

      // Clear linked payers when the new payer can't lend its rates to them (CLE-2751)
      if (field === "payerId") {
        const newPayer = payers?.data?.find((p: any) => p.id === value);
        const nextLinkableType = linkedPayerTypeForContractPayer(
          newPayer?.type
        );
        if (nextLinkableType === null) {
          updates.medicareAdvantagePayerIds = [];
        } else {
          updates.medicareAdvantagePayerIds = (
            prev.medicareAdvantagePayerIds || []
          ).filter((id) => {
            const linked = payers?.data?.find((p: any) => p.id === id);
            return linked?.type === nextLinkableType && id !== value;
          });
        }
      }

      const next = { ...prev, ...updates };
      // CLE-2781 — clearing the next update date, or switching off Percent Increase, must not
      // leave a term or a percent stranded. Re-derived on every change so the form state is
      // always a body the API will accept.
      return { ...next, ...normalizeUpdateTerms(next.nextUpdateDate, next) };
    });
  };

  const resetNewRateCalc = () =>
    setNewRateCalc({
      baseRateFileId: "",
      ratePercent: 100,
      rateType: "percent",
    });

  const addNewRateCalculation = () => {
    if (newRateCalc.ratePercent <= 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Invalid Rate Calculation",
        detail:
          newRateCalc.rateType === "unit"
            ? "Please enter a valid unit price."
            : "Please enter a valid rate percentage.",
      });
      return;
    }

    // A carveout base rate file attaches as a ContractCarveout (with its %); a
    // normal base rate file becomes a ContractRateCalculation.
    const selectedFile = newRateCalc.baseRateFileId
      ? baseRateFileById.get(newRateCalc.baseRateFileId)
      : undefined;

    if (selectedFile?.isCarveout) {
      if (
        pendingCarveouts.some((c) => c.baseRateFileId === selectedFile.id) ||
        attachedCarveoutBaseRateIds.has(selectedFile.id)
      ) {
        resetNewRateCalc();
        return;
      }
      setPendingCarveouts((prev) => [
        ...prev,
        {
          baseRateFileId: selectedFile.id,
          ratePercent: Number(newRateCalc.ratePercent),
          rateType: newRateCalc.rateType,
        },
      ]);
      resetNewRateCalc();
      return;
    }

    const newCalc: RateCalculation = {
      id: `temp-${Date.now()}`, // Temporary ID for UI purposes
      baseRateFileId: newRateCalc.baseRateFileId || undefined,
      ratePercent: Number(newRateCalc.ratePercent),
      rateType: newRateCalc.rateType,
      sortOrder: formData.rateCalculations.length,
    };

    // Add the new rate calculation to the form data
    setFormData((prev) => ({
      ...prev,
      rateCalculations: [...prev.rateCalculations, newCalc],
    }));

    resetNewRateCalc();
  };

  const removeRateCalculation = (index: number) => {
    // Remove from form data
    setFormData((prev) => ({
      ...prev,
      rateCalculations: prev.rateCalculations.filter((_, i) => i !== index),
    }));
  };

  // CLE-2789 — the replace confirmation, as the app's own dialog (ConfirmDialog is mounted
  // globally in AppLayout) rather than a browser alert. Promise-wrapped so the save flow can
  // await it; closing the dialog any other way counts as Cancel.
  const confirmReplace = (conflicts: IOwnContractConflict[]) =>
    new Promise<boolean>((resolve) => {
      const copy = replaceConfirmCopy(conflicts);
      // The save flow has the global loader up; it would sit on top of this dialog and block
      // its buttons. Nothing is loading while we wait for an answer, so drop it, and put it
      // back only if the user goes ahead (Cancel returns to the form, where the outer
      // `finally` clears it anyway).
      setIsLoading(false);
      confirmDialog({
        header: "Replace existing contract?",
        icon: "pi pi-exclamation-triangle",
        message: (
          <div>
            <p className="mb-2">
              <strong>{copy.names}</strong> {copy.title}
            </p>
            <p className={copy.carried ? "mb-2 text-600" : "mb-0 text-600"}>
              {copy.body}
            </p>
            {copy.carried && (
              <p className="mb-0 text-orange-700 font-medium">{copy.carried}</p>
            )}
          </div>
        ),
        acceptLabel: "Replace",
        rejectLabel: "Cancel",
        acceptClassName: "p-button-danger",
        accept: () => {
          setIsLoading(true);
          resolve(true);
        },
        reject: () => resolve(false),
        onHide: () => resolve(false),
      });
    });

  // CLE-2800 — a chip's "x" is easy to hit without noticing, and the save then deletes every
  // rate the provider drew from this contract, silently. Same dialog mechanics as Replace.
  const confirmDisconnect = (removed: { id: string; name: string }[]) =>
    new Promise<boolean>((resolve) => {
      const copy = disconnectConfirmCopy(removed, selectedPayer?.name);
      setIsLoading(false);
      confirmDialog({
        header:
          removed.length === 1
            ? "Disconnect provider?"
            : "Disconnect providers?",
        icon: "pi pi-exclamation-triangle",
        message: (
          <div>
            <p className="mb-2">
              Remove <strong>{copy.names}</strong> from this contract?
            </p>
            <p className="mb-0 text-600">{copy.body}</p>
          </div>
        ),
        acceptLabel: "Disconnect",
        rejectLabel: "Keep connected",
        acceptClassName: "p-button-danger",
        accept: () => {
          setIsLoading(true);
          resolve(true);
        },
        reject: () => resolve(false),
        onHide: () => resolve(false),
      });
    });

  const handleSubmit = async () => {
    if (!actualContractId) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Missing contract identifier.",
      });
      return;
    }
    const contractId = actualContractId;

    // Validation
    if (!formData.providerId) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select a provider.",
      });
      return;
    }

    // CLE-2781 — a percent increase with no percent is the one update-term state the form can
    // still reach (the field is required but nothing blocks an empty submit).
    if (
      formData.nextUpdateDate &&
      formData.updateTerm === "PercentIncrease" &&
      formData.updatePercent === null
    ) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail:
          "Enter the percent increase, or choose a different update term.",
      });
      return;
    }

    if (!formData.payerId) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select a payer.",
      });
      return;
    }

    if (!formData.effectiveDate) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Effective date is required.",
      });
      return;
    }

    const effectiveIso = localCalendarDateToIsoDateString(
      formData.effectiveDate
    );
    if (!effectiveIso) {
      toastElement?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Effective date is invalid.",
      });
      return;
    }

    const duplicatesAnotherTerm = termsInGroup?.some((t) => {
      if (t.id === actualContractId) return false;
      const other = normalizeToIsoDateOnly(t.effectiveDate);
      return other !== undefined && other === effectiveIso;
    });
    if (duplicatesAnotherTerm) {
      return;
    }

    try {
      setIsLoading(true);
      setIsSubmitting(true);

      const updateData = {
        providerId: formData.providerId,
        payerId: formData.payerId,
        effectiveDate: effectiveIso,
        // Null, not undefined, when the picker is empty — the API reads undefined as "leave the
        // stored date alone", which is why the date could never be removed (Ani, CLE-2781).
        nextUpdateDate:
          localCalendarDateToIsoDateString(formData.nextUpdateDate) ?? null,
        // CLE-2781 — sent explicitly (null included) so clearing a term actually clears it;
        // the API treats `undefined` as "leave the column alone".
        ...normalizeUpdateTerms(formData.nextUpdateDate, formData),
        rateCalculations: formData.rateCalculations.map((calc) => ({
          baseRateFileId: calc.baseRateFileId,
          ratePercent: Number(calc.ratePercent),
          rateType: calc.rateType ?? "percent",
          sortOrder: calc.sortOrder || 0,
        })),
        medicareAdvantagePayerIds: canLinkPayers
          ? formData.medicareAdvantagePayerIds
          : undefined,
        // CLE-2786 — always sent, so clearing the picker actually removes the links (and the
        // rows generated for those providers). Undefined would leave them untouched.
        linkedProviderIds: withoutContractProvider(
          formData.linkedProviderIds,
          formData.providerId
        ),
        notes: formData.notes || "",
        implantTerms: formData.implantTerms || "",
      };

      // CLE-2789 — connect-and-replace. If the save is refused because a connected provider
      // still holds its own contract for this payer, offer to replace it: the API writes that
      // provider's new rates first and deletes its old contract second, so there is never a
      // moment with no rates. The message is only the trigger; ids come from the conflicts
      // endpoint, never from the text.
      // The list the re-save sends. Replace can widen it: providers connected to a retired
      // contract move to this one, and an update REPLACES the whole link set — re-saving with
      // only the user's picks would unlink them again and delete their rows.
      // CLE-2800 — confirm before a save drops a connected provider (its rows go with it).
      const linkedProviderIdsBefore = (
        loadedContract?.linkedProviders ?? []
      ).map((p) => p.id);
      const disconnectedIds = providersRemoved(
        linkedProviderIdsBefore,
        updateData.linkedProviderIds
      );
      if (disconnectedIds.length > 0) {
        const disconnected = disconnectedIds.map(
          (id) =>
            (loadedContract?.linkedProviders ?? []).find(
              (p) => p.id === id
            ) ?? {
              id,
              name: "a connected provider",
            }
        );
        if (!(await confirmDisconnect(disconnected))) return;
      }

      let linkedProviderIdsForSave = updateData.linkedProviderIds;
      const saveContract = () =>
        updateContract({
          id: contractId,
          ...updateData,
          linkedProviderIds: linkedProviderIdsForSave,
        }).unwrap();
      try {
        await saveContract();
      } catch (saveError: any) {
        const message =
          saveError?.data?.messages?.[0] || saveError?.data?.message || "";
        if (!isOwnContractConflictMessage(message)) throw saveError;

        const { data: conflicts } = await getLinkedProviderConflicts({
          id: contractId,
          providerIds: updateData.linkedProviderIds,
        }).unwrap();

        if (conflicts.contractStatus !== "Active") {
          toastElement?.show({
            severity: "warn",
            summary: "Activate first",
            detail: REPLACE_NEEDS_ACTIVE_CONTRACT,
          });
          return;
        }
        if (conflicts.ownContract.length === 0) throw saveError;
        if (!(await confirmReplace(conflicts.ownContract))) return;

        // One at a time on purpose: each replace writes rows and then deletes a contract.
        await conflicts.ownContract.reduce<Promise<void>>(
          async (previous, conflict) => {
            await previous;
            const { data: replaced } = await replaceLinkedProvider({
              id: contractId,
              providerId: conflict.providerId,
            }).unwrap();
            // Whatever the API now has linked (picked + carried over) is what the re-save keeps.
            linkedProviderIdsForSave = Array.from(
              new Set([
                ...linkedProviderIdsForSave,
                ...(replaced.linkedProviders ?? []).map((p) => p.id),
              ])
            );
          },
          Promise.resolve()
        );
        await saveContract();
      }

      // CLE-2573 — corrected rates for already attached carve-outs.
      const queuedRateEdits = Object.entries(carveoutRateEdits);
      if (queuedRateEdits.length > 0) {
        try {
          await Promise.all(
            queuedRateEdits.map(([carveoutId, rate]) =>
              updateCarveoutRate({
                contractId,
                carveoutId,
                ratePercent: rate.ratePercent,
                rateType: rate.rateType,
              }).unwrap()
            )
          );
          setCarveoutRateEdits({});
        } catch {
          toastElement?.show({
            severity: "warn",
            summary: "Contract Updated",
            detail:
              "Contract was updated but one or more carve-out rates failed to save. Try again from this page.",
          });
        }
      }

      if (pendingCarveouts.length > 0) {
        try {
          await attachCarveoutBaseRateFiles({
            contractId,
            carveouts: pendingCarveouts,
          }).unwrap();
          setPendingCarveouts([]);
        } catch {
          toastElement?.show({
            severity: "warn",
            summary: "Contract Updated",
            detail:
              "Contract was updated but attaching carveout file(s) failed. Try again from this page.",
          });
        }
      }

      // Upload attachment files if any were selected
      if (formData.attachmentFiles.length > 0) {
        try {
          toastElement?.show({
            severity: "info",
            summary: "Uploading Attachments",
            detail: `Uploading ${formData.attachmentFiles.length} attachment file(s)...`,
          });

          const attachmentPromises = formData.attachmentFiles.map((file) =>
            uploadAttachment({
              contractId,
              file,
            }).unwrap()
          );

          await Promise.all(attachmentPromises);
        } catch (attachmentError) {
          toastElement?.show({
            severity: "warn",
            summary: "Contract Updated",
            detail:
              "Contract was updated but some attachment files failed to upload.",
          });
        }
      }

      // CLE-2800 — a provider connected to an Active contract got its rates in this save; say so,
      // because "connected but no rates until Regenerate" is what this replaced.
      const connectedNames =
        loadedContract?.status === "Active"
          ? providersAdded(
              linkedProviderIdsBefore,
              linkedProviderIdsForSave
            ).map(
              (id) =>
                connectedProviderOptions.find((o) => o.value === id)?.label ??
                id
            )
          : [];
      const connectedText =
        connectedNames.length > 0
          ? ` Rates generated for ${connectedNames.join(", ")}.`
          : "";
      if (formData.attachmentFiles.length === 0) {
        toastElement?.show({
          severity: "success",
          summary: "Contract Updated",
          detail: `Contract fee schedule has been updated successfully.${connectedText}`,
        });
      } else {
        toastElement?.show({
          severity: "success",
          summary: "Contract Updated",
          detail: "Contract fee schedule and files uploaded successfully.",
        });
      }

      const contractUrl = hospitalId
        ? `/hospitals/view/${hospitalId}/contracts/${contractId}`
        : `/payer-rules/contracts/${contractId}`;
      navigate(contractUrl);
    } catch (error: any) {
      const errorMessage =
        error?.data?.messages?.[0] ||
        error?.data?.message ||
        "Failed to update contract fee schedule.";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
      });
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  const getRateTypeForDisplay = (calc: RateCalculation): RateType =>
    normalizeRateType(calc.rateType);

  const handleRemoveExistingCarveout = async (carveoutId: string) => {
    if (!actualContractId) return;
    try {
      setIsLoading(true);
      await deleteCarveout({
        contractId: actualContractId,
        carveoutId,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Carveout Removed",
        detail: "Carveout file has been removed successfully.",
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.messages?.[0] ||
        error?.data?.message ||
        "Failed to remove carveout file.";
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * One row per attached rate source — base rate calculations and carveouts
   * merged into a single list. Carveouts (saved or pending) render with a tag
   * and their own percentage; each row carries its own removal handler.
   */
  interface IUnifiedRateRow {
    key: string;
    name: string;
    isCarveout: boolean;
    ratePercent: number;
    rateType: RateType;
    pending: boolean;
    /** CLE-2573 — an attached carve-out whose corrected rate is queued for save. */
    pendingRate?: boolean;
    onRemove: () => void;
    /** CLE-2573 — stage a corrected rate for this row; written on save. */
    onSaveRate: (ratePercent: number, rateType: RateType) => void;
    /** Source base rate / carveout file id, so the row can open the update modal. */
    editFileId?: string;
    /**
     * CLE-2567 — convert this row's file between base rate and carve-out. Only
     * possible while the file is not attached to a saved contract, so in practice
     * this works for rows added in this session and shows the blocker for the rest.
     */
    onConvert?: () => void;
  }

  /**
   * CLE-2567 — converting a file that sits in this form (not yet saved to the
   * contract) leaves the local row on the wrong side, and the save would then be
   * rejected by the API's carve-out/base-rate check. So move the row to the other
   * list as part of the conversion.
   */
  const convertRateCalculationRow = (index: number) => {
    const calc = formData.rateCalculations[index];
    if (!calc?.baseRateFileId) return;
    setFormData((prev) => ({
      ...prev,
      rateCalculations: prev.rateCalculations.filter((_, i) => i !== index),
    }));
    setPendingCarveouts((prev) => [
      ...prev,
      {
        baseRateFileId: calc.baseRateFileId as string,
        ratePercent: Number(calc.ratePercent),
        rateType: normalizeRateType(calc.rateType),
      },
    ]);
  };

  const convertPendingCarveoutRow = (index: number) => {
    const carveout = pendingCarveouts[index];
    if (!carveout) return;
    setPendingCarveouts((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      rateCalculations: [
        ...prev.rateCalculations,
        {
          id: `temp-${Date.now()}`,
          baseRateFileId: carveout.baseRateFileId,
          ratePercent: carveout.ratePercent ?? 100,
          rateType: carveout.rateType ?? "percent",
          sortOrder: prev.rateCalculations.length,
        },
      ],
    }));
  };

  const buildUnifiedRateRows = (): IUnifiedRateRow[] => {
    const rows: IUnifiedRateRow[] = [];

    formData.rateCalculations.forEach((calc, index) => {
      rows.push({
        key: calc.id ?? `rc-${index}`,
        name: resolveRateCalculationBaseRateDisplayName(
          calc,
          baseRateFiles?.data
        ),
        isCarveout: false,
        ratePercent: Number(calc.ratePercent),
        rateType: getRateTypeForDisplay(calc),
        pending: false,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onRemove: calc.id ? () => removeRateCalculation(index) : () => {},
        onSaveRate: (ratePercent, rateType) =>
          setFormData((prev) => ({
            ...prev,
            rateCalculations: prev.rateCalculations.map((rc, i) =>
              i === index ? { ...rc, ratePercent, rateType } : rc
            ),
          })),
        editFileId: calc.baseRateFileId,
        onConvert: calc.baseRateFileId
          ? () =>
              openConvertDialog(
                {
                  id: calc.baseRateFileId as string,
                  displayName: resolveRateCalculationBaseRateDisplayName(
                    calc,
                    baseRateFiles?.data
                  ),
                  isCarveout: false,
                },
                {
                  warning:
                    formData.rateCalculations.length === 1
                      ? "This is the only base rate file on this contract. A carve-out only overrides the CPT codes it lists, so the contract would be left with no base pricing."
                      : undefined,
                  onConverted: (_isCarveout, result) => {
                    if (
                      result.migratedContracts.some(
                        (c) => c.id === actualContractId
                      )
                    ) {
                      // Saved row: the API already created the carve-out. Drop the
                      // stale base rate row so a save before the refetch does not
                      // re-send a file the API now rejects as a carve-out.
                      setFormData((prev) => ({
                        ...prev,
                        rateCalculations: prev.rateCalculations.filter(
                          (_, i) => i !== index
                        ),
                      }));
                    } else {
                      convertRateCalculationRow(index);
                    }
                  },
                }
              )
          : undefined,
      });
    });

    (loadedContract?.carveOutFiles ?? []).forEach((c) => {
      const queuedRate = carveoutRateEdits[c.id];
      rows.push({
        key: `co-${c.id}`,
        name: c.fileName,
        isCarveout: true,
        ratePercent:
          queuedRate?.ratePercent ??
          (c.ratePercent != null ? Number(c.ratePercent) : 100),
        rateType: queuedRate?.rateType ?? normalizeRateType(c.rateType),
        pending: false,
        pendingRate: queuedRate !== undefined,
        onRemove: () => handleRemoveExistingCarveout(c.id),
        onSaveRate: (ratePercent, rateType) =>
          setCarveoutRateEdits((prev) => ({
            ...prev,
            [c.id]: { ratePercent, rateType },
          })),
        editFileId: c.baseRateFileId ?? undefined,
        onConvert: c.baseRateFileId
          ? () =>
              openConvertDialog(
                {
                  id: c.baseRateFileId as string,
                  displayName: c.fileName,
                  isCarveout: true,
                },
                {
                  onConverted: (_isCarveout, result) => {
                    if (
                      !result.migratedContracts.some(
                        (m) => m.id === actualContractId
                      )
                    ) {
                      return;
                    }
                    // The API turned this carve-out into a rate calculation.
                    // Mirror it into the form: a save before the refetch would
                    // otherwise replace the rate rows without it and delete it.
                    setFormData((prev) => ({
                      ...prev,
                      rateCalculations: [
                        ...prev.rateCalculations,
                        {
                          id: `temp-${Date.now()}`,
                          baseRateFileId: c.baseRateFileId as string,
                          ratePercent:
                            c.ratePercent != null ? Number(c.ratePercent) : 100,
                          rateType: normalizeRateType(c.rateType),
                          sortOrder: prev.rateCalculations.length,
                        },
                      ],
                    }));
                  },
                }
              )
          : undefined,
      });
    });

    pendingCarveouts.forEach((c, index) => {
      rows.push({
        key: `pco-${c.baseRateFileId}`,
        name:
          baseRateFileById.get(c.baseRateFileId)?.displayName ??
          c.baseRateFileId,
        isCarveout: true,
        ratePercent: c.ratePercent != null ? Number(c.ratePercent) : 100,
        rateType: c.rateType ?? "percent",
        pending: true,
        onRemove: () =>
          setPendingCarveouts((prev) => prev.filter((_, i) => i !== index)),
        onSaveRate: (ratePercent, rateType) =>
          setPendingCarveouts((prev) =>
            prev.map((pc, i) =>
              i === index ? { ...pc, ratePercent, rateType } : pc
            )
          ),
        editFileId: c.baseRateFileId,
        onConvert: () =>
          openConvertDialog(
            {
              id: c.baseRateFileId,
              displayName: baseRateFileById.get(c.baseRateFileId)?.displayName,
              isCarveout: true,
            },
            {
              onConverted: (_isCarveout, result) => {
                if (
                  !result.migratedContracts.some(
                    (c) => c.id === actualContractId
                  )
                ) {
                  convertPendingCarveoutRow(index);
                }
              },
            }
          ),
      });
    });

    return rows;
  };

  const unifiedFileBody = (row: IUnifiedRateRow) => (
    <div className="flex align-items-center gap-2">
      <span className="font-medium">{row.name}</span>
      {row.pending && (
        <span className="text-500 text-sm">(will attach on save)</span>
      )}
      {row.pendingRate && (
        <span className="text-500 text-sm">(rate applies on save)</span>
      )}
    </div>
  );

  const unifiedTypeBody = (row: IUnifiedRateRow) =>
    row.isCarveout ? (
      <Tag value="Carve-out" severity="warning" />
    ) : (
      <Tag value="Base rate" severity="info" />
    );

  const handleSaveRate = (
    row: IUnifiedRateRow,
    ratePercent: number,
    rateType: RateType
  ) => {
    if (ratePercent <= 0) {
      toastElement?.show({
        severity: "warn",
        summary: "Invalid Rate",
        detail:
          rateType === "unit"
            ? "Please enter a valid unit price."
            : "Please enter a valid rate percentage.",
      });
      return;
    }
    row.onSaveRate(ratePercent, rateType);
    setEditingRateKey(null);
  };

  const unifiedRateBody = (row: IUnifiedRateRow) => (
    <RateCell
      ratePercent={row.ratePercent}
      rateType={row.rateType}
      editing={editingRateKey === row.key}
      onBeginEdit={() => setEditingRateKey(row.key)}
      onCancel={() => setEditingRateKey(null)}
      onSave={(ratePercent, rateType) =>
        handleSaveRate(row, ratePercent, rateType)
      }
    />
  );

  const unifiedActionsBody = (row: IUnifiedRateRow) => (
    <div className="flex align-items-center">
      {row.editFileId && (
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text"
          tooltip="Edit / new version of this file"
          tooltipOptions={{ position: "top" }}
          onClick={() => setUpdateDialogFileId(row.editFileId ?? null)}
        />
      )}
      {row.onConvert && (
        <Button
          icon={row.isCarveout ? "pi pi-file" : "pi pi-file-edit"}
          className="p-button-rounded p-button-text"
          tooltip={
            row.isCarveout
              ? "Convert to base rate file"
              : "Convert to carve-out file"
          }
          tooltipOptions={{ position: "top" }}
          onClick={row.onConvert}
        />
      )}
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger p-button-text"
        onClick={row.onRemove}
      />
    </div>
  );

  const attachmentFileActionsTemplate = (rowData: any) => (
    <Button
      icon="pi pi-times"
      className="p-button-rounded p-button-text p-button-danger p-button-sm"
      onClick={async () => {
        if (!actualContractId) return;
        try {
          setIsLoading(true);
          await deleteAttachment({
            contractId: actualContractId,
            attachmentId: rowData.id,
          }).unwrap();
          toastElement?.show({
            severity: "success",
            summary: "Attachment Removed",
            detail: "Attachment file has been removed successfully.",
          });
        } catch (error: any) {
          const errorMessage =
            error?.data?.messages?.[0] ||
            error?.data?.message ||
            "Failed to remove attachment file.";
          toastElement?.show({
            severity: "error",
            summary: "Error",
            detail: errorMessage,
          });
        } finally {
          setIsLoading(false);
        }
      }}
    />
  );

  const newAttachmentFileActionsTemplate = (rowData: File, options: any) => (
    <Button
      icon="pi pi-times"
      className="p-button-rounded p-button-text p-button-danger p-button-sm"
      onClick={() => {
        const newFiles = formData.attachmentFiles.filter(
          (_, index) => index !== options.rowIndex
        );
        handleInputChange("attachmentFiles", newFiles);
      }}
    />
  );

  if (isLoadingContract) {
    return <div>Loading...</div>;
  }

  const contract = contractData?.data;
  const isActivated = contract?.status !== "Draft";

  const navigateToTermEdit = (newId: string) => {
    const base = hospitalId
      ? `/hospitals/view/${hospitalId}/contracts`
      : `/payer-rules/contracts`;
    navigate(`${base}/${newId}/edit`, { replace: true, state: location.state });
  };

  return (
    <div className="card">
      <div className="mb-3">
        <Button
          label="Return to Contract Details"
          icon="pi pi-arrow-left"
          className="p-button-text p-button-plain text-600"
          onClick={() => {
            const contractUrl = hospitalId
              ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}`
              : `/payer-rules/contracts/${actualContractId}`;
            navigate(contractUrl);
          }}
        />
      </div>
      <div className="mb-4 flex justify-content-between align-items-start flex-wrap gap-2">
        <h2 className="m-0">Edit Contract Fee Schedule</h2>
        <div className="flex gap-2 align-items-center flex-wrap">
          {termSwitcherOptions.length > 1 && (
            <Dropdown
              value={actualContractId}
              options={termSwitcherOptions}
              onChange={(e) => navigateToTermEdit(e.value)}
              placeholder="Switch term"
              className="min-w-12rem"
            />
          )}
        </div>
      </div>

      <div className="grid">
        {/* Basic Information */}
        <div className="col-12">
          <Card title="Basic Information" className="mb-4">
            <div className="grid">
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Provider *
                </label>
                <Dropdown
                  value={formData.providerId}
                  options={providerOptions}
                  onChange={(e) => handleInputChange("providerId", e.value)}
                  placeholder="Select Provider"
                  className="w-full"
                  filter
                  disabled={isActivated}
                />
              </div>
              <div className="col-12 md:col-6">
                <label className="block text-900 font-medium mb-2">
                  Payer *
                </label>
                <Dropdown
                  value={formData.payerId}
                  options={payerOptions}
                  onChange={(e) => handleInputChange("payerId", e.value)}
                  placeholder="Select Payer"
                  className="w-full"
                  filter
                  disabled={isActivated}
                />
              </div>
              <div className="col-12 md:col-4">
                <label className="block text-900 font-medium mb-2">
                  Effective Date *
                </label>
                <Calendar
                  value={formData.effectiveDate}
                  onChange={(e) => handleInputChange("effectiveDate", e.value)}
                  placeholder="Select Effective Date"
                  className={
                    effectiveDateDuplicatesOtherTerm
                      ? "w-full p-invalid"
                      : "w-full"
                  }
                  showIcon
                />
                {effectiveDateDuplicatesOtherTerm && (
                  <small className="text-red-500 block mt-1">
                    Another term in this contract already uses this effective
                    date. Choose a different day.
                  </small>
                )}
              </div>
              <div className="col-12 md:col-4">
                <label className="block text-900 font-medium mb-2">
                  Next Update Date
                </label>
                <Calendar
                  value={formData.nextUpdateDate}
                  onChange={(e) => handleInputChange("nextUpdateDate", e.value)}
                  placeholder="Select Next Update Date"
                  className="w-full"
                  showIcon
                />
              </div>
              <UpdateTermFields
                nextUpdateDate={formData.nextUpdateDate}
                updateTerm={formData.updateTerm}
                updatePercent={formData.updatePercent}
                onUpdateTermChange={(v) => handleInputChange("updateTerm", v)}
                onUpdatePercentChange={(v) =>
                  handleInputChange("updatePercent", v)
                }
                fieldClassName="col-12 md:col-4"
              />
              {/* CLE-2788 — implant terms, above notes; shown as its own table column. */}
              <div className="col-12">
                <label className="block text-900 font-medium mb-2">
                  Implant Terms
                </label>
                <InputTextarea
                  value={formData.implantTerms || ""}
                  onChange={(e) =>
                    handleInputChange("implantTerms", e.target.value)
                  }
                  placeholder="e.g. Invoice cost + 10%, capped at $5,000 per case"
                  rows={2}
                  className="w-full"
                  autoResize
                />
              </div>
              <div className="col-12">
                <label className="block text-900 font-medium mb-2">Notes</label>
                <InputTextarea
                  value={formData.notes || ""}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  placeholder="Enter any notes or special instructions for this contract..."
                  rows={4}
                  className="w-full"
                  autoResize
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Linked payers - only when this contract's payer can lend its rates (CLE-2751) */}
        {canLinkPayers && (
          <div className="col-12">
            <Card title={linkedCopy.title} className="mb-4">
              <p className="mb-3 text-600">{linkedCopy.help}</p>
              <MultiSelect
                value={formData.medicareAdvantagePayerIds}
                options={medicareAdvantagePayerOptions}
                onChange={(e) =>
                  handleInputChange("medicareAdvantagePayerIds", e.value)
                }
                placeholder={linkedCopy.placeholder}
                className="w-full"
                display="chip"
                showClear
                filter
              />
              <small className="text-600 mt-2 block">{linkedCopy.note}</small>
            </Card>
          </div>
        )}

        {/* Connected providers — one contract shared across providers/accounts (CLE-2786) */}
        <div className="col-12">
          <Card title={LINKED_PROVIDERS_COPY.title} className="mb-4">
            {/* Half width like the other form fields; one line of help under the picker. */}
            <div className="grid">
              <div className="col-12 md:col-6">
                <MultiSelect
                  value={formData.linkedProviderIds}
                  options={connectedProviderOptions}
                  onChange={(e) =>
                    handleInputChange("linkedProviderIds", e.value)
                  }
                  placeholder={LINKED_PROVIDERS_COPY.placeholder}
                  className="w-full"
                  display="chip"
                  showClear
                  filter
                />
                <small className="text-600 mt-2 block">
                  {LINKED_PROVIDERS_COPY.note}
                </small>
              </div>
            </div>
          </Card>
        </div>

        {/* Rate Calculations (base rate files + carve-outs) */}
        <div className="col-12">
          <Card title="Rate Calculations" className="mb-4">
            <p className="mb-3 text-600">
              Attach base rate files and carve-outs. Carve-out files (tagged
              below) override specific CPT rates; both take a percentage.
            </p>
            <div className="mb-4">
              <div className="grid">
                <div className="col-12 md:col-5">
                  <label className="block text-900 font-medium mb-2">
                    Base Rate / Carve-Out File
                  </label>
                  <Dropdown
                    value={newRateCalc.baseRateFileId}
                    options={baseRateFileOptions}
                    onChange={(e) => {
                      if (e.value === UPLOAD_NEW_SENTINEL) {
                        setShowUploadDialog(true);
                        return;
                      }
                      setNewRateCalc((prev) => ({
                        ...prev,
                        baseRateFileId: e.value,
                      }));
                    }}
                    placeholder="Select file"
                    className="w-full"
                    filter
                  />
                </div>
                <div className="col-12 md:col-2">
                  <label className="block text-900 font-medium mb-2">
                    Type
                  </label>
                  <Dropdown
                    value={newRateCalc.rateType}
                    options={[
                      { label: "$", value: "unit" },
                      { label: "%", value: "percent" },
                    ]}
                    onChange={(e) => {
                      const newType = (e.value ?? "percent") as RateType;
                      setNewRateCalc((prev) => ({
                        ...prev,
                        rateType: newType,
                        ratePercent: 100,
                      }));
                    }}
                    placeholder="Select type"
                    className="w-full"
                  />
                </div>
                <div className="col-12 md:col-3">
                  <label className="block text-900 font-medium mb-2">
                    {newRateCalc.rateType === "unit"
                      ? "Price per Unit"
                      : "Rate Percentage"}
                  </label>
                  <div className="p-inputgroup">
                    <InputNumber
                      value={
                        newRateCalc.rateType === "unit"
                          ? newRateCalc.ratePercent / 100
                          : newRateCalc.ratePercent
                      }
                      onValueChange={(e) =>
                        setNewRateCalc((prev) => ({
                          ...prev,
                          ratePercent:
                            prev.rateType === "unit"
                              ? (e.value || 0) * 100
                              : e.value || 0,
                        }))
                      }
                      placeholder={
                        newRateCalc.rateType === "unit"
                          ? "Price per Unit"
                          : "Rate %"
                      }
                      className="w-full"
                      mode="decimal"
                      min={0}
                      max={100000}
                      minFractionDigits={0}
                      maxFractionDigits={
                        newRateCalc.rateType === "unit" ? 4 : 2
                      }
                    />
                    {newRateCalc.rateType !== "unit" && (
                      <span className="p-inputgroup-addon">%</span>
                    )}
                  </div>
                </div>
                <div className="col-12 md:col-2 flex align-items-end">
                  <Button
                    label="Add"
                    icon="pi pi-plus"
                    onClick={addNewRateCalculation}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <DataTable
              value={buildUnifiedRateRows()}
              dataKey="key"
              emptyMessage="No rate calculations added yet."
              className="mt-3"
            >
              <Column header="File" body={unifiedFileBody} />
              <Column
                header="Type"
                body={unifiedTypeBody}
                style={{ width: "130px" }}
              />
              <Column
                header="Rate"
                body={unifiedRateBody}
                style={{ width: "280px" }}
              />
              <Column
                header="Actions"
                body={unifiedActionsBody}
                style={{ width: "100px" }}
              />
            </DataTable>
          </Card>
        </div>
        {/* MPDR & Case Rate Rulesets (applied automatically by provider + payer) */}
        <div className="col-12">
          <Card title="MPDR & Case Rate Rulesets" className="mb-4">
            <p className="mb-3 text-600">
              Adjustment rules are not selected per contract — they apply
              automatically based on the provider and payer.
            </p>
            {/* MPDR Rule Sets */}
            <div className="mb-4">
              {/* Show existing MPDR rulesets if provider/payer selected */}
              {existingMpdrRulesets.length > 0 && (
                <div className="mt-3 p-3 border-1 border-300 border-round bg-gray-50">
                  <h6 className="mt-0 mb-2">
                    Existing MPDR Rule Sets for {selectedProvider?.name} -{" "}
                    {formData.payerId &&
                      payers?.data?.find((p: any) => p.id === formData.payerId)
                        ?.name}
                  </h6>
                  <p className="text-sm text-600 mb-3">
                    These rulesets apply to this provider + payer combination
                    automatically:
                  </p>
                  {existingMpdrRulesets.map((ruleset, index) => (
                    <div
                      key={ruleset.id}
                      className={
                        index < existingMpdrRulesets.length - 1
                          ? "pb-2 mb-2 border-bottom"
                          : ""
                      }
                    >
                      <div className="font-medium">
                        <a
                          href={`/payer-rules/mppr/edit/${ruleset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            // Only navigate if in admin context (hospital staff can't access these pages)
                            if (!hospitalId) {
                              navigate(`/payer-rules/mppr/edit/${ruleset.id}`);
                            }
                          }}
                          className="text-primary no-underline hover:underline cursor-pointer"
                        >
                          {ruleset.groupName || "Unnamed MPDR Rule Set"}
                        </a>
                      </div>
                      <div className="text-sm text-600 mt-1">
                        Rank 2: {ruleset.discountPercentForRank2}%, Rank 3:{" "}
                        {ruleset.discountPercentForRank3}%, Rank 4:{" "}
                        {ruleset.discountPercentForRank4}%, Rank 5+:{" "}
                        {ruleset.discountPercentForRank5Plus}%
                      </div>
                      <div className="text-sm text-600">
                        {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                          <span>
                            CPT Codes:{" "}
                            {ruleset.cptCodes.slice(0, 30).join(", ")}
                            {ruleset.cptCodes.length > 30 && (
                              <span>
                                , and {ruleset.cptCodes.length - 30} other CPT
                                codes
                              </span>
                            )}
                          </span>
                        ) : (
                          "No CPT codes configured"
                        )}
                        {ruleset.allowOnDuplicateCptCodes &&
                          " • Allows duplicate codes"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-top pt-4">
              {/* Case Rate Rule Sets */}
              {/* Show existing case rate rulesets if provider/payer selected */}
              {existingCaseRateRulesets.length > 0 && (
                <div className="mt-3 p-3 border-1 border-300 border-round bg-gray-50">
                  <h6 className="mt-0 mb-2">
                    Existing Case Rate Rule Sets for {selectedProvider?.name} -{" "}
                    {formData.payerId &&
                      payers?.data?.find((p: any) => p.id === formData.payerId)
                        ?.name}
                  </h6>
                  <p className="text-sm text-600 mb-3">
                    These rulesets apply to this provider + payer combination
                    automatically:
                  </p>
                  {existingCaseRateRulesets.map((ruleset, index) => (
                    <div
                      key={ruleset.id}
                      className={
                        index < existingCaseRateRulesets.length - 1
                          ? "pb-2 mb-2 border-bottom"
                          : ""
                      }
                    >
                      <div className="font-medium">
                        <a
                          href={`/payer-rules/case-rate/edit/${ruleset.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(
                              `/payer-rules/case-rate/edit/${ruleset.id}`
                            );
                          }}
                          className="text-primary no-underline hover:underline cursor-pointer"
                        >
                          {ruleset.groupName || "Unnamed Case Rate Rule Set"}
                        </a>
                      </div>
                      <div className="text-sm text-600 mt-1">
                        {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                          <div>
                            Included CPT Codes:{" "}
                            {ruleset.cptCodes.slice(0, 30).join(", ")}
                            {ruleset.cptCodes.length > 30 && (
                              <span>
                                , and {ruleset.cptCodes.length - 30} other CPT
                                codes
                              </span>
                            )}
                          </div>
                        ) : (
                          <div>No included CPT codes</div>
                        )}
                        {ruleset.excludedCptCodes &&
                        ruleset.excludedCptCodes.length > 0 ? (
                          <div>
                            Excluded CPT Codes:{" "}
                            {ruleset.excludedCptCodes.slice(0, 30).join(", ")}
                            {ruleset.excludedCptCodes.length > 30 && (
                              <span>
                                , and {ruleset.excludedCptCodes.length - 30}{" "}
                                other CPT codes
                              </span>
                            )}
                          </div>
                        ) : (
                          <div>No excluded CPT codes</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Inpatient Rulesets */}
        <div className="col-12">
          <Card title="Inpatient Rulesets" className="mb-4">
            <p className="mb-3 text-600">
              Inpatient rulesets apply automatically based on the provider and
              payer.
            </p>

            {existingInpatientRulesets.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-gray-50">
                <h6 className="mt-0 mb-2">
                  Existing Inpatient Rulesets for {selectedProvider?.name} -{" "}
                  {formData.payerId &&
                    payers?.data?.find((p: any) => p.id === formData.payerId)
                      ?.name}
                </h6>
                <p className="text-sm text-600 mb-3">
                  These rulesets apply to this provider + payer combination
                  automatically:
                </p>
                {existingInpatientRulesets.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingInpatientRulesets.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/inpatient-rulesets/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!hospitalId) {
                            navigate(
                              `/payer-rules/inpatient-rulesets/edit/${ruleset.id}`
                            );
                          }
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.name || "Unnamed Inpatient Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      Type: {ruleset.type || "—"}
                    </div>
                    <div className="text-sm text-600">
                      DRG Codes:{" "}
                      {ruleset.drgCodes && ruleset.drgCodes.length > 0 ? (
                        <span>
                          DRG Codes: {ruleset.drgCodes.slice(0, 30).join(", ")}
                          {ruleset.drgCodes.length > 30 && (
                            <span>
                              , and {ruleset.drgCodes.length - 30} other DRG
                              codes
                            </span>
                          )}
                        </span>
                      ) : (
                        "No codes configured"
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Exploding Fee Schedule Rulesets */}
        <div className="col-12">
          <Card title="Exploding Fee Schedule Rulesets" className="mb-4">
            <p className="mb-3 text-600">
              Exploding fee schedule rulesets apply automatically based on the
              provider and payer.
            </p>

            {existingExplodingRulesets.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-gray-50">
                <h6 className="mt-0 mb-2">
                  Existing Exploding Fee Schedule Rulesets for{" "}
                  {selectedProvider?.name} -{" "}
                  {formData.payerId &&
                    payers?.data?.find((p: any) => p.id === formData.payerId)
                      ?.name}
                </h6>
                <p className="text-sm text-600 mb-3">
                  These rulesets apply to this provider + payer combination
                  automatically:
                </p>
                {existingExplodingRulesets.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingExplodingRulesets.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!hospitalId) {
                            navigate(
                              `/payer-rules/exploding-fee-schedules/edit/${ruleset.id}`
                            );
                          }
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName ||
                          "Unnamed Exploding Fee Schedule Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      {ruleset.cptCodes && ruleset.cptCodes.length > 0 ? (
                        <span>
                          CPT Codes: {ruleset.cptCodes.slice(0, 30).join(", ")}
                          {ruleset.cptCodes.length > 30 && (
                            <span>
                              , and {ruleset.cptCodes.length - 30} other CPT
                              codes
                            </span>
                          )}
                        </span>
                      ) : (
                        "No codes configured"
                      )}
                    </div>
                    <div className="text-sm text-600">
                      Total Rate: $
                      {ruleset.totalRateInCents != null
                        ? (ruleset.totalRateInCents / 100).toLocaleString(
                            "en-US",
                            { minimumFractionDigits: 2 }
                          )
                        : "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Package Rulesets */}
        <div className="col-12">
          <Card title="Package Rulesets" className="mb-4">
            <p className="mb-3 text-600">
              Package rulesets apply automatically based on the provider and
              payer.
            </p>

            {existingPackageRulesets.length > 0 && (
              <div className="mt-3 p-3 border-1 border-300 border-round bg-gray-50">
                <h6 className="mt-0 mb-2">
                  Existing Package Rulesets for {selectedProvider?.name} -{" "}
                  {formData.payerId &&
                    payers?.data?.find((p: any) => p.id === formData.payerId)
                      ?.name}
                </h6>
                <p className="text-sm text-600 mb-3">
                  These rulesets apply to this provider + payer combination
                  automatically:
                </p>
                {existingPackageRulesets.map((ruleset, index) => (
                  <div
                    key={ruleset.id}
                    className={
                      index < existingPackageRulesets.length - 1
                        ? "pb-2 mb-2 border-bottom"
                        : ""
                    }
                  >
                    <div className="font-medium">
                      <a
                        href={`/payer-rules/packages/edit/${ruleset.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!hospitalId) {
                            navigate(
                              `/payer-rules/packages/edit/${ruleset.id}`
                            );
                          }
                        }}
                        className="text-primary no-underline hover:underline cursor-pointer"
                      >
                        {ruleset.groupName || "Unnamed Package Ruleset"}
                      </a>
                    </div>
                    <div className="text-sm text-600 mt-1">
                      {ruleset.triggerCptCodes &&
                      ruleset.triggerCptCodes.length > 0 ? (
                        <span>
                          Trigger CPT Codes:{" "}
                          {ruleset.triggerCptCodes.slice(0, 30).join(", ")}
                          {ruleset.triggerCptCodes.length > 30 && (
                            <span>
                              , and {ruleset.triggerCptCodes.length - 30} other
                              Trigger CPT codes codes
                            </span>
                          )}
                        </span>
                      ) : (
                        "No trigger codes configured"
                      )}
                    </div>
                    <div className="text-sm text-600">
                      {ruleset.bundledCptCodes &&
                      ruleset.bundledCptCodes.length > 0 ? (
                        <span>
                          Bundled CPT Codes:{" "}
                          {ruleset.bundledCptCodes.slice(0, 30).join(", ")}
                          {ruleset.bundledCptCodes.length > 30 && (
                            <span>
                              , and {ruleset.bundledCptCodes.length - 30} other
                              Bundled CPT codes codes
                            </span>
                          )}
                        </span>
                      ) : (
                        "No bundled codes configured"
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Contract Attachments */}
        <div className="col-12">
          <Card title="Contract Attachments" className="mb-4">
            <p className="mb-3 text-600">
              Attach files to this contract for reference and download (PDFs,
              Excel, images, etc. up to 50MB each).
            </p>

            {/* Existing attachments */}
            {contract?.attachments && contract.attachments.length > 0 && (
              <div className="mb-4">
                <h6>Existing Attachment Files</h6>
                <DataTable value={contract.attachments}>
                  <Column field="fileName" header="File Name" />
                  <Column
                    field="fileSize"
                    header="Size"
                    body={(rowData) => {
                      const kb = rowData.fileSize / 1024;
                      return kb >= 1024
                        ? `${(kb / 1024).toFixed(2)} MB`
                        : `${kb.toFixed(2)} KB`;
                    }}
                  />
                  <Column
                    field="createdAt"
                    header="Uploaded"
                    body={(rowData) =>
                      new Date(rowData.createdAt).toLocaleDateString()
                    }
                  />
                  <Column
                    header="Actions"
                    body={attachmentFileActionsTemplate}
                    style={{ width: "100px" }}
                  />
                </DataTable>
              </div>
            )}

            {/* Upload new attachments */}
            <FileUpload
              name="attachments"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.bmp,.webp,.ppt,.pptx,.odt,.ods,.odp,.zip"
              maxFileSize={52428800}
              emptyTemplate={
                <div className="text-center p-4">
                  <i className="pi pi-cloud-upload text-4xl text-400" />
                  <p className="mt-2 mb-0">
                    Drag and drop files here or click to browse.
                  </p>
                </div>
              }
              mode="basic"
              chooseOptions={{
                label: "Select Files",
                icon: "pi pi-fw pi-plus",
              }}
              onSelect={(e) => {
                const files = Array.from(e.files as File[] | ArrayLike<File>);
                handleInputChange("attachmentFiles", [
                  ...formData.attachmentFiles,
                  ...files,
                ]);
              }}
            />

            {/* Display selected new attachment files */}
            {formData.attachmentFiles.length > 0 && (
              <div className="mt-4">
                <h6>New Files to Upload ({formData.attachmentFiles.length})</h6>
                <DataTable value={formData.attachmentFiles}>
                  <Column
                    header="File Name"
                    body={(rowData: File) => rowData.name}
                  />
                  <Column
                    field="size"
                    header="Size"
                    body={(rowData) => {
                      const kb = rowData.size / 1024;
                      return kb >= 1024
                        ? `${(kb / 1024).toFixed(2)} MB`
                        : `${kb.toFixed(2)} KB`;
                    }}
                  />
                  <Column
                    header="Actions"
                    body={newAttachmentFileActionsTemplate}
                  />
                </DataTable>
              </div>
            )}
          </Card>
        </div>

        {/* Actions */}
        <div className="col-12">
          <div className="flex justify-content-between">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                const contractUrl = hospitalId
                  ? `/hospitals/view/${hospitalId}/contracts/${actualContractId}`
                  : `/payer-rules/contracts/${actualContractId}`;
                navigate(contractUrl);
              }}
            />
            <Button
              label="Save Changes"
              icon="pi pi-check"
              onClick={handleSubmit}
              disabled={isSubmitting || effectiveDateDuplicatesOtherTerm}
              loading={isSubmitting}
            />
          </div>
        </div>
      </div>
      <BaseRateFileUploadDialog
        visible={showUploadDialog}
        onHide={() => setShowUploadDialog(false)}
        accountId={hospitalId ?? undefined}
        onUploaded={(file) => {
          // Just select the freshly uploaded file in the dropdown; the user
          // then picks the type/rate and clicks Add (same as any other file).
          // The base rate files query is cache-invalidated on upload, so the
          // file appears as an option without a page refresh.
          setNewRateCalc((prev) => ({ ...prev, baseRateFileId: file.id }));
        }}
      />
      <BaseRateFileUpdateDialog
        visible={updateDialogFileId != null}
        fileId={updateDialogFileId}
        accountId={hospitalId ?? undefined}
        allowNewTerm={isLatestTerm}
        onHide={() => setUpdateDialogFileId(null)}
      />
    </div>
  );
}

export default EditContractFeeSchedulePage;
